// Children — the org admin's kids management (migration 20261022000008;
// replaces "Roster oversight").
//
// WHAT AN ADMIN SEES. Each child as an opaque, org-scoped code, the team
// members supporting them (adults, already visible on Users), whether sharing
// is on, ACTIVITY (did the family's app sync play in the last 30 days), and the
// family's licence state as far as this org is concerned. NEVER progress: there
// is no stage, step, skill or count of anything a child did on this screen.
//
// TWO ACTIONS. "Move to inactive" is the org's own filing (a child who left the
// centre) and changes nothing for the family. "End support" removes every link
// this org's members hold to the child, with the suggestions they sent; the
// family keeps everything and can link again with a new code — the confirm step
// says exactly that.
//
// FAMILY CONSENT + STAFF (migration 0012). A family with a licence from this
// org can share a child with the ORGANISATION when they sign in to the app. Only
// then can an admin assign staff (assign_child_staff); a child the family has
// not shared shows that plainly and offers nothing to do. The admin still sees
// no play here; only the staff they assign do, in their own caregiver portal.

import { useId, useRef, useEffect, useState } from "react";
import { useComfort } from "../ComfortContext";
import { fill, getDictionary } from "../../i18n/dictionary";
import { getPortalData } from "../../data";
import { STAFF_ROLES, type OrgChildEntry, type OrgChildStaff, type StaffRole } from "../../data/types";
import type { LinkRole } from "../../domain/types";
import { useOrg } from "./OrgContext";
import {
  Button,
  Card,
  Chip,
  ConfirmDialog,
  ErrorPanel,
  INPUT,
  LoadingLine,
  Notice,
  PageHeader,
  TableCard,
  Tabs,
  TD,
  TH,
  errorCode,
  useDemoVariant,
  useLoad,
  type Tone,
} from "./ui";

type T = ReturnType<typeof getDictionary>["admin"]["kids"];

export function roleLabel(t: Pick<T, "roleFamily" | "roleTherapist" | "roleTeacher" | "roleCaregiver" | "roleOther">, role: LinkRole | null | undefined): string {
  switch (role) {
    case "family":
      return t.roleFamily;
    case "therapist":
      return t.roleTherapist;
    case "teacher":
      return t.roleTeacher;
    case "caregiver":
      return t.roleCaregiver;
    default:
      return t.roleOther;
  }
}

const activity = (t: T, a: OrgChildEntry["activity"]): { tone: Tone; label: string } =>
  a === "active" ? { tone: "good", label: t.actActive } : a === "inactive" ? { tone: "neutral", label: t.actInactive } : { tone: "muted", label: t.actUnknown };

const licence = (t: T, l: OrgChildEntry["licence"]): { tone: Tone; label: string } =>
  l === "licensed" ? { tone: "info", label: t.licLicensed } : l === "paid" ? { tone: "good", label: t.licPaid } : { tone: "neutral", label: t.licFree };

const sharing = (t: T, s: OrgChildEntry["sharingStatus"]): { tone: Tone; label: string } =>
  s === "active" ? { tone: "good", label: t.shareActive } : s === "pending" ? { tone: "attention", label: t.sharePending } : { tone: "muted", label: t.shareRevoked };

const consentChip = (t: T, c: OrgChildEntry["consent"]): { tone: Tone; label: string } =>
  c === "shared" ? { tone: "good", label: t.consentShared } : { tone: "neutral", label: t.consentNot };

/** assign_child_staff / unassign_child_staff refusals in calm words. */
export function staffErrorCopy(t: T, notAuth: string, err: unknown): string {
  const code = errorCode(err);
  if (code === "42501") return notAuth;
  const msg = err instanceof Error ? err.message.toLowerCase() : "";
  if (code === "P0001" && msg.includes("not shared")) return t.staffErrNotShared;
  if (code === "P0001" && msg.includes("directly")) return t.staffErrDirect;
  if (code === "P0001" && msg.includes("not a member")) return t.staffErrNotMember;
  return t.errGeneric;
}

type Tab = "active" | "inactive";

export function Kids() {
  const { locale } = useComfort();
  const dict = getDictionary(locale);
  const t = dict.admin.kids;
  const { orgId } = useOrg();
  const variant = useDemoVariant();
  const load = useLoad(() => getPortalData().orgChildren(orgId), [orgId, variant]);

  const [tab, setTab] = useState<Tab>("active");
  const [notice, setNotice] = useState("");
  const [rowError, setRowError] = useState<{ ref: string; text: string } | null>(null);
  const [busyRef, setBusyRef] = useState<string | null>(null);
  const [ending, setEnding] = useState<OrgChildEntry | null>(null);
  const [endBusy, setEndBusy] = useState(false);
  const [endError, setEndError] = useState("");
  const [staffFor, setStaffFor] = useState<string | null>(null);

  const errText = (err: unknown) => (errorCode(err) === "42501" ? dict.admin.licences.errNotAuth : t.errGeneric);

  async function move(k: OrgChildEntry) {
    if (busyRef) return;
    setBusyRef(k.childRef);
    setRowError(null);
    setNotice("");
    try {
      await getPortalData().setOrgChildArchived(orgId, k.childRef, !k.archived);
      setNotice(k.archived ? t.movedActive : t.movedInactive);
      await load.refresh();
    } catch (err) {
      setRowError({ ref: k.childRef, text: errText(err) });
    } finally {
      setBusyRef(null);
    }
  }

  async function confirmEnd() {
    if (!ending || endBusy) return;
    setEndBusy(true);
    setEndError("");
    try {
      const n = await getPortalData().endOrgSupport(orgId, ending.childRef);
      setEnding(null);
      setNotice(fill(t.endedOk, { n }));
      await load.refresh();
    } catch (err) {
      setEndError(errText(err));
    } finally {
      setEndBusy(false);
    }
  }

  const header = (
    <>
      <PageHeader title={t.title} subtitle={t.subtitle} />
      <p style={{ margin: "10px 0 0", fontSize: 13.5, lineHeight: 1.5, color: "var(--ink-soft)", maxWidth: "70ch" }}>{t.refNote}</p>
    </>
  );

  if (load.loading && !load.data) {
    return (
      <>
        {header}
        <LoadingLine label={t.loading} />
      </>
    );
  }
  if (load.error || !load.data) {
    return (
      <>
        {header}
        <div style={{ marginTop: 20 }}>
          <ErrorPanel title={t.errorTitle} message={errText(load.error)} onRetry={load.reload} retryLabel={t.retry} />
        </div>
      </>
    );
  }

  const all = load.data;
  const activeKids = all.filter((k) => !k.archived);
  const inactiveKids = all.filter((k) => k.archived);
  const shown = tab === "active" ? activeKids : inactiveKids;

  return (
    <>
      {header}
      <div style={{ marginTop: 18, display: "flex", flexDirection: "column", gap: 12 }}>
        <Tabs
          label={t.title}
          value={tab}
          onChange={(k) => {
            setTab(k);
            setNotice("");
          }}
          tabs={[
            { key: "active", label: fill(t.tabActive, { n: activeKids.length }) },
            { key: "inactive", label: fill(t.tabInactive, { n: inactiveKids.length }) },
          ]}
        />
        <div role="status" aria-live="polite">
          {notice && <Notice tone="good">{notice}</Notice>}
        </div>
        <p style={{ margin: 0, fontSize: 13, color: "var(--ink-faint)" }}>
          {tab === "active" ? t.actNote : t.inactiveNote}
        </p>

        {shown.length === 0 ? (
          <Card>
            <b style={{ display: "block", color: "var(--ink-soft)", fontSize: 15 }}>{tab === "active" ? t.emptyActive : t.emptyInactive}</b>
            <p style={{ margin: "6px 0 0", color: "var(--ink-faint)", fontSize: 14, lineHeight: 1.55 }}>
              {tab === "active" ? t.emptyActiveBody : t.emptyInactiveBody}
            </p>
          </Card>
        ) : (
          <TableCard label={t.title} minWidth={980}>
            <thead>
              <tr>
                <th scope="col" style={TH}>{t.colChild}</th>
                <th scope="col" style={TH}>{t.colSupported}</th>
                <th scope="col" style={TH}>{t.colConsent}</th>
                <th scope="col" style={TH}>{t.colSharing}</th>
                <th scope="col" style={TH}>{t.colActivity}</th>
                <th scope="col" style={TH}>{t.colLicence}</th>
                <th scope="col" style={TH}>{t.colActions}</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((k) => {
                const a = activity(t, k.activity);
                const l = licence(t, k.licence);
                const s = sharing(t, k.sharingStatus);
                const cs = consentChip(t, k.consent);
                const open = staffFor === k.childRef;
                return (
                  <tr key={k.childRef}>
                    <td style={{ ...TD, fontWeight: 600, color: "var(--ink-deep)", whiteSpace: "nowrap" }}>{fill(t.childCode, { ref: k.childRef })}</td>
                    <td style={{ ...TD, minWidth: 210 }}>
                      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                        <span style={{ overflowWrap: "break-word" }}>{k.supportedBy.join(", ")}</span>
                        <span style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                          {k.roles.map((r) => (
                            <Chip key={r} tone="neutral">
                              {roleLabel(t, r)}
                            </Chip>
                          ))}
                        </span>
                      </div>
                    </td>
                    <td style={{ ...TD, minWidth: 170 }}>
                      <div style={{ display: "flex", flexDirection: "column", gap: 6, alignItems: "flex-start" }}>
                        <Chip tone={cs.tone}>{cs.label}</Chip>
                        {k.consent === "shared" ? (
                          <Button
                            variant="secondary"
                            aria-expanded={open}
                            aria-controls="kid-staff-panel"
                            onClick={() => setStaffFor(open ? null : k.childRef)}
                          >
                            {open ? t.closeStaff : fill(t.assignStaff, { n: k.staffCount })}
                          </Button>
                        ) : (
                          <span style={{ fontSize: 12.5, color: "var(--ink-faint)", lineHeight: 1.45, maxWidth: "26ch" }}>{t.notSharedNote}</span>
                        )}
                      </div>
                    </td>
                    <td style={TD}><Chip tone={s.tone}>{s.label}</Chip></td>
                    <td style={TD}><Chip tone={a.tone}>{a.label}</Chip></td>
                    <td style={TD}><Chip tone={l.tone}>{l.label}</Chip></td>
                    <td style={TD}>
                      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                        <Button variant="secondary" disabled={busyRef !== null} onClick={() => void move(k)}>
                          {k.archived ? t.moveActive : t.moveInactive}
                        </Button>
                        <Button variant="secondary" disabled={busyRef !== null} onClick={() => setEnding(k)}>
                          {t.endSupport}
                        </Button>
                      </div>
                      {rowError?.ref === k.childRef && (
                        <p role="alert" style={{ margin: "6px 0 0", fontSize: 13, color: "var(--peach-deep)", fontWeight: 600 }}>
                          {rowError.text}
                        </p>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </TableCard>
        )}

        {staffFor && shown.some((k) => k.childRef === staffFor && k.consent === "shared") && (
          <StaffPanel
            key={staffFor}
            orgId={orgId}
            childRef={staffFor}
            onChanged={() => void load.refresh()}
            onClose={() => setStaffFor(null)}
          />
        )}
      </div>

      {ending && (
        <ConfirmDialog
          title={t.endTitle}
          body={
            <>
              <b style={{ color: "var(--ink-deep)" }}>{fill(t.childCode, { ref: ending.childRef })}</b>
              <br />
              {t.endBody}
            </>
          }
          confirmLabel={t.endButton}
          cancelLabel={dict.common.cancel}
          busy={endBusy}
          error={endError}
          onConfirm={() => void confirmEnd()}
          onCancel={() => {
            setEnding(null);
            setEndError("");
          }}
        />
      )}
    </>
  );
}

// ---- Assign staff ---------------------------------------------------------------
//
// One shared child at a time, under the table so it fits a phone. Lists who is
// assigned (Remove), and adds any member of the organisation with a role.

function StaffPanel({
  orgId,
  childRef,
  onChanged,
  onClose,
}: {
  orgId: string;
  childRef: string;
  onChanged: () => void;
  onClose: () => void;
}) {
  const { locale } = useComfort();
  const dict = getDictionary(locale);
  const t = dict.admin.kids;
  const notAuth = dict.admin.licences.errNotAuth;
  const variant = useDemoVariant();
  const memberId = useId();
  const roleId = useId();
  const headingRef = useRef<HTMLHeadingElement>(null);
  const load = useLoad(async () => {
    const ds = getPortalData();
    const [staff, members] = await Promise.all([ds.orgChildStaff(orgId, childRef), ds.orgMembers(orgId)]);
    return { staff, members };
  }, [orgId, childRef, variant]);
  const [account, setAccount] = useState("");
  const [role, setRole] = useState<StaffRole>("teacher");
  const [busy, setBusy] = useState<string | null>(null);
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  const staff: OrgChildStaff[] = load.data?.staff ?? [];
  const assigned = new Set(staff.map((s) => s.accountId));
  const choices = (load.data?.members ?? []).filter((m) => !assigned.has(m.accountId));

  async function assign() {
    if (!account || busy) return;
    setBusy("assign");
    setError("");
    setNotice("");
    try {
      await getPortalData().assignChildStaff(orgId, childRef, account, role);
      const email = choices.find((m) => m.accountId === account)?.email ?? "";
      setAccount("");
      setNotice(fill(t.staffAssigned, { email }));
      await load.refresh();
      onChanged();
    } catch (err) {
      setError(staffErrorCopy(t, notAuth, err));
    } finally {
      setBusy(null);
    }
  }

  async function remove(s: OrgChildStaff) {
    if (busy) return;
    setBusy(s.accountId);
    setError("");
    setNotice("");
    try {
      await getPortalData().unassignChildStaff(orgId, childRef, s.accountId);
      setNotice(t.staffRemoved);
      await load.refresh();
      onChanged();
    } catch (err) {
      setError(staffErrorCopy(t, notAuth, err));
    } finally {
      setBusy(null);
    }
  }

  return (
    <section id="kid-staff-panel" aria-labelledby={`${memberId}-title`} style={{ display: "flex", flexDirection: "column" }}>
      <Card>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
          <h2 id={`${memberId}-title`} ref={headingRef} tabIndex={-1} style={{ fontSize: 18, color: "var(--ink-deep)", margin: 0 }}>
            {fill(t.staffTitle, { child: fill(t.childCode, { ref: childRef }) })}
          </h2>
          <Button variant="quiet" onClick={onClose}>
            {t.closeStaff}
          </Button>
        </div>
        <p style={{ margin: "8px 0 14px", fontSize: 14, color: "var(--ink-soft)", lineHeight: 1.55 }}>{t.staffIntro}</p>

        {load.loading && !load.data ? (
          <LoadingLine label={t.staffLoading} />
        ) : load.error ? (
          <ErrorPanel title={t.errorTitle} message={staffErrorCopy(t, notAuth, load.error)} onRetry={load.reload} retryLabel={t.retry} />
        ) : (
          <>
            {staff.length === 0 ? (
              <p style={{ margin: 0, color: "var(--ink-soft)" }}>{t.staffNone}</p>
            ) : (
              <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 8 }}>
                {staff.map((s) => (
                  <li
                    key={s.accountId}
                    style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", paddingBlock: 6, borderBottom: "1px solid var(--line)" }}
                  >
                    <span style={{ flex: "1 1 180px", minWidth: 0, overflowWrap: "anywhere", fontWeight: 600, color: "var(--ink-deep)" }}>{s.email}</span>
                    <Chip tone="neutral">{roleLabel(t, s.role)}</Chip>
                    <Button
                      variant="secondary"
                      aria-label={fill(t.staffRemoveLabel, { email: s.email })}
                      disabled={busy !== null}
                      onClick={() => void remove(s)}
                    >
                      {t.staffRemove}
                    </Button>
                  </li>
                ))}
              </ul>
            )}

            <h3 style={{ fontSize: 15.5, color: "var(--ink-deep)", margin: "18px 0 10px" }}>{t.staffAdd}</h3>
            {choices.length === 0 ? (
              <p style={{ margin: 0, color: "var(--ink-soft)", fontSize: 14 }}>{t.staffNoMembers}</p>
            ) : (
              <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end" }}>
                <div style={{ display: "flex", flexDirection: "column", gap: 5, flex: "1 1 220px", minWidth: 0 }}>
                  <label htmlFor={memberId} style={{ fontSize: 13.5, fontWeight: 600, color: "var(--ink-soft)" }}>
                    {t.staffMember}
                  </label>
                  <select id={memberId} value={account} onChange={(e) => setAccount(e.target.value)} style={INPUT}>
                    <option value="">{t.staffChoose}</option>
                    {choices.map((m) => (
                      <option key={m.accountId} value={m.accountId}>
                        {m.email}
                      </option>
                    ))}
                  </select>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 5, flex: "0 1 180px", minWidth: 0 }}>
                  <label htmlFor={roleId} style={{ fontSize: 13.5, fontWeight: 600, color: "var(--ink-soft)" }}>
                    {t.staffRole}
                  </label>
                  <select id={roleId} value={role} onChange={(e) => setRole(e.target.value as StaffRole)} style={INPUT}>
                    {STAFF_ROLES.map((r) => (
                      <option key={r} value={r}>
                        {roleLabel(t, r)}
                      </option>
                    ))}
                  </select>
                </div>
                <Button onClick={() => void assign()} disabled={!account || busy !== null}>
                  {t.staffAssign}
                </Button>
              </div>
            )}
          </>
        )}

        <div role="status" aria-live="polite" style={{ marginTop: notice ? 12 : 0 }}>
          {notice && <Notice tone="good">{notice}</Notice>}
        </div>
        {error && (
          <p role="alert" style={{ margin: "12px 0 0", fontSize: 14, color: "var(--peach-deep)", fontWeight: 600 }}>
            {error}
          </p>
        )}
      </Card>
    </section>
  );
}
